"use client";

import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import {
  Grid,
  TextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { enqueueSnackbar } from "notistack";
import { PhoneCodeSelector } from "@/components/General/PhoneCodeSelector";
import useGeneralStore from "@/store/General/useGeneralStore";
import GeneralService from "@/core/services/GeneralService";
import { SupportRequest } from "@/core/models/General.model";
import { parseJsonBody } from "@/lib/general.utils";

const DonateNowComponent = () => {
  const { t, i18n } = useTranslation("common");
  const router = useRouter();
  const isRtl = i18n.language === "ar";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneCode, setPhoneCode] = useState("+20");

  const { GovernoratesComponent, fetchGovernoratesComponent } = useGeneralStore(
    (state) => ({
      GovernoratesComponent: state.GovernoratesComponent,
      fetchGovernoratesComponent: state.fetchGovernoratesComponent,
    })
  );

  // Get amount from query params
  const amountParam = router.query.amount as string;
  const donationAmount = amountParam ? parseFloat(amountParam) || 0 : 0;

  useEffect(() => {
    if (!GovernoratesComponent) {
      fetchGovernoratesComponent();
    }
  }, [GovernoratesComponent, fetchGovernoratesComponent, i18n.language]);

  const governoratesJson = GovernoratesComponent
    ? parseJsonBody(GovernoratesComponent.jsonBody)
    : null;
  const governorates = governoratesJson?.governorates || [];

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required(t("donateNow.firstNameRequired"))
      .min(2, t("donateNow.firstNameInvalid")),
    lastName: Yup.string()
      .required(t("donateNow.lastNameRequired"))
      .min(2, t("donateNow.lastNameInvalid")),
    country: Yup.string().required(t("donateNow.countryRequired")),
    phone: Yup.string()
      .required(t("donateNow.phoneRequired"))
      .min(8, t("donateNow.phoneInvalid")),
    email: Yup.string()
      .email(t("donateNow.emailInvalid"))
      .required(t("donateNow.emailRequired")),
    orderNotes: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      country: "مصر",
      phone: "",
      email: "",
      orderNotes: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const supportData: Omit<SupportRequest, "id" | "insertDate"> = {
          name: `${values.firstName} ${values.lastName}`,
          email: values.email,
          phone: `${phoneCode}${values.phone}`,
          subject: "Donation",
          body: JSON.stringify({
            firstName: values.firstName,
            lastName: values.lastName,
            country: values.country,
            amount: donationAmount,
            orderNotes: values.orderNotes,
          }),
        };

        const response = await GeneralService.AddSupport("Donate", {
          ...supportData,
          id: 0,
          insertDate: "",
        } as SupportRequest);

        if (response?.success) {
          enqueueSnackbar(
            t("donateNow.success") || "Donation submitted successfully!",
            {
              variant: "success",
            }
          );
          formik.resetForm();
          router.push("/donate");
        } else {
          throw new Error(response?.message || "Failed to submit donation");
        }
      } catch (error: any) {
        console.error("Error submitting donation:", error);
        enqueueSnackbar(
          error?.message ||
            t("donateNow.error") ||
            "Failed to submit donation. Please try again.",
          {
            variant: "error",
          }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="bg-gray-50 min-h-screen py-[2rem] px-[1rem] md:px-[2rem]">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <form
            onSubmit={formik.handleSubmit}
            className="w-full"
            dir={isRtl ? "rtl" : "ltr"}
          >
            <Grid container>
              {/* Left Column - Billing and Shipping */}
              <Grid item xs={12} md={7}>
                <Box className="bg-white p-[2rem] rounded-lg shadow-sm">
                  <Typography
                    variant="h5"
                    className="text-[1.5rem] md:text-[2rem] font-bold text-primary mb-[1.5rem]"
                  >
                    {t("donateNow.billingAndShipping")}
                  </Typography>

                  <Grid container>
                    {/* First Name */}
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        required
                        error={
                          formik.touched.firstName &&
                          Boolean(formik.errors.firstName)
                        }
                      >
                        <TextField
                          id="firstName"
                          name="firstName"
                          label={t("donateNow.firstName")}
                          placeholder={t("donateNow.firstName")}
                          size="small"
                          required
                          InputLabelProps={{ shrink: true }}
                          value={formik.values.firstName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.firstName &&
                            Boolean(formik.errors.firstName)
                          }
                          helperText={
                            formik.touched.firstName &&
                            (formik.errors.firstName as string)
                          }
                          className="rounded-lg"
                          inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                        />
                      </FormControl>
                    </Grid>

                    {/* Last Name */}
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        required
                        error={
                          formik.touched.lastName &&
                          Boolean(formik.errors.lastName)
                        }
                      >
                        <TextField
                          id="lastName"
                          name="lastName"
                          label={t("donateNow.lastName")}
                          placeholder={t("donateNow.lastName")}
                          size="small"
                          required
                          InputLabelProps={{ shrink: true }}
                          value={formik.values.lastName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.lastName &&
                            Boolean(formik.errors.lastName)
                          }
                          helperText={
                            formik.touched.lastName &&
                            (formik.errors.lastName as string)
                          }
                          className="rounded-lg"
                          inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                        />
                      </FormControl>
                    </Grid>

                    {/* Country */}
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        required
                        error={
                          formik.touched.country &&
                          Boolean(formik.errors.country)
                        }
                      >
                        <InputLabel
                          id="country-label"
                          shrink={true}
                          className="!text-[0.875rem]"
                        >
                          {t("donateNow.country")}
                        </InputLabel>
                        <Select
                          id="country"
                          name="country"
                          labelId="country-label"
                          value={formik.values.country}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.country &&
                            Boolean(formik.errors.country)
                          }
                          size="small"
                          className="rounded-lg"
                          displayEmpty
                          label={t("donateNow.country")}
                        >
                          <MenuItem value="مصر">مصر</MenuItem>
                          <MenuItem value="السعودية">السعودية</MenuItem>
                          <MenuItem value="الكويت">الكويت</MenuItem>
                          <MenuItem value="الإمارات">الإمارات</MenuItem>
                        </Select>
                        {formik.touched.country && formik.errors.country && (
                          <div className="text-[#e57d7a] text-sm mt-1">
                            {formik.errors.country as string}
                          </div>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Phone */}
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        required
                        error={
                          formik.touched.phone && Boolean(formik.errors.phone)
                        }
                      >
                        <div className="flex gap-2">
                          <div className="w-[8rem]">
                            <PhoneCodeSelector
                              value={phoneCode}
                              onChange={setPhoneCode}
                            />
                          </div>
                          <div className="flex-1">
                            <TextField
                              id="phone"
                              name="phone"
                              label={t("donateNow.phone")}
                              placeholder={t("donateNow.phone")}
                              type="tel"
                              size="small"
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              value={formik.values.phone}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.phone &&
                                Boolean(formik.errors.phone)
                              }
                              helperText={
                                formik.touched.phone &&
                                (formik.errors.phone as string)
                              }
                              className="rounded-lg"
                              inputProps={{ dir: "ltr" }}
                            />
                          </div>
                        </div>
                      </FormControl>
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        required
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                      >
                        <TextField
                          id="email"
                          name="email"
                          label={t("donateNow.email")}
                          placeholder={t("donateNow.email")}
                          type="email"
                          size="small"
                          required
                          InputLabelProps={{ shrink: true }}
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.email && Boolean(formik.errors.email)
                          }
                          helperText={
                            formik.touched.email &&
                            (formik.errors.email as string)
                          }
                          className="rounded-lg"
                          inputProps={{ dir: "ltr" }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Right Column - Additional Information and Order Summary */}
              <Grid item xs={12} md={5}>
                <Grid container>
                  {/* Additional Information */}
                  <Grid item xs={12}>
                    <Box className="bg-white p-[2rem] rounded-lg shadow-sm">
                      <Typography
                        variant="h5"
                        className="text-[1.5rem] md:text-[2rem] font-bold text-primary mb-[1.5rem]"
                      >
                        {t("donateNow.additionalInformation")}
                      </Typography>
                      <FormControl fullWidth>
                        <TextField
                          id="orderNotes"
                          name="orderNotes"
                          label={t("donateNow.orderNotes")}
                          placeholder={t("donateNow.orderNotesPlaceholder")}
                          multiline
                          rows={6}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={formik.values.orderNotes}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="rounded-lg"
                          inputProps={{ dir: isRtl ? "rtl" : "ltr" }}
                        />
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* Your Order */}
                  <Grid item xs={12}>
                    <Box className="bg-white p-[2rem] rounded-lg shadow-sm">
                      <Typography
                        variant="h5"
                        className="text-[1.5rem] md:text-[2rem] font-bold text-primary mb-[1.5rem]"
                      >
                        {t("donateNow.yourOrder")}
                      </Typography>

                      <div className="space-y-[1rem]">
                        <div className="flex justify-between items-center pb-[1rem] border-b">
                          <Typography variant="body2" className="text-gray-600">
                            {t("donateNow.product")}
                          </Typography>
                          <Typography variant="body2" className="text-gray-600">
                            {t("donateNow.donation")} x1
                          </Typography>
                        </div>

                        <div className="flex justify-between items-center pt-[1rem]">
                          <Typography
                            variant="body1"
                            className="font-medium text-gray-700"
                          >
                            {t("donateNow.subtotal")}
                          </Typography>
                          <Typography
                            variant="body1"
                            className="font-medium text-gray-900"
                          >
                            {donationAmount.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            {t("currency")}
                          </Typography>
                        </div>

                        <div className="flex justify-between items-center pt-[1rem] border-t">
                          <Typography
                            variant="h6"
                            className="font-bold text-gray-900"
                          >
                            {t("donateNow.total")}
                          </Typography>
                          <Typography
                            variant="h6"
                            className="font-bold text-primary"
                          >
                            {donationAmount.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            {t("currency")}
                          </Typography>
                        </div>
                      </div>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Payment Section */}
              <Grid item xs={12}>
                <Box className="bg-white p-[2rem] rounded-lg shadow-sm">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-[2rem]">
                    <div className="flex items-center gap-[1rem]">
                      <Typography
                        variant="h6"
                        className="text-primary font-bold text-[1.2rem]"
                      >
                        paymob
                      </Typography>
                    </div>
                    <div className="flex flex-col gap-[1rem] w-full md:w-auto">
                      <Typography
                        variant="body1"
                        className="font-medium text-gray-700"
                      >
                        {t("donateNow.payWithPaymob")}
                      </Typography>
                      <Box className="bg-gray-100 p-[1rem] rounded-lg">
                        <Typography
                          variant="body2"
                          className="text-gray-600 text-center"
                        >
                          {t("donateNow.payWithPaymob")}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        className="text-gray-500 text-center md:text-right"
                      >
                        {t("donateNow.privacyPolicyText")}
                      </Typography>
                    </div>
                  </div>
                </Box>
              </Grid>

              {/* Confirm Order Button */}
              <Grid item xs={12}>
                <LoadingButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  loading={isSubmitting}
                  className="!bg-secondary hover:!bg-secondary/80 !py-[1rem] !text-[1rem] !font-semibold !text-white !normal-case !rounded-lg"
                >
                  {t("donateNow.confirmOrder")}
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default DonateNowComponent;
